import { ArrowRight, ShieldCheck, BadgeCheck, TrendingUp } from "lucide-react";
import Reveal from "@/components/Reveal";
import Logo from "@/components/Logo";
import { useI18n } from "@/lib/i18n";

export default function Hero() {
  const { t } = useI18n();
  const stats = [
    { k: "14", v: "Loan Products" },
    { k: "6.5%", v: "Starting Rate" },
    { k: "0", v: "CIBIL Needed" },
    { k: "Quick", v: "Approval Time" },
  ];

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] banner-navy text-white overflow-hidden noise-overlay isolate"
    >
      {/* Decorative gold rings */}
      <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-[#D4AF37]/15 float-slow" />
      <div className="pointer-events-none absolute -bottom-32 -left-40 w-[420px] h-[420px] rounded-full border border-[#D4AF37]/10" />
      <div className="pointer-events-none absolute top-1/3 left-1/2 w-[700px] h-[700px] rounded-full border border-[#D4AF37]/5 -translate-x-1/2" />

      {/* Subtle gold grid */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06]" aria-hidden="true">
        <defs>
          <pattern id="grid" width="48" height="48" patternUnits="userSpaceOnUse">
            <path d="M 48 0 L 0 0 0 48" fill="none" stroke="#D4AF37" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#grid)" />
      </svg>

      <div className="relative w-full gutter pt-24 md:pt-32 lg:pt-40 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-8 md:gap-10 items-start">
          {/* Left copy */}
          <div className="lg:col-span-7 text-left min-w-0">
            <Reveal>
              <div className="inline-flex max-w-full items-start gap-2 px-3 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/5 text-[10px] sm:text-[11px] uppercase tracking-[0.16em] sm:tracking-[0.22em] text-[#F0D788] leading-relaxed">
                <span className="w-1.5 h-1.5 mt-1.5 shrink-0 rounded-full bg-[#D4AF37] pulse-gold" />
                <span className="min-w-0">{t("hero.badge")}</span>
              </div>
            </Reveal>

            <Reveal delay={120}>
              <h1
                data-testid="hero-heading"
                className="font-display fluid-display font-medium mt-6 md:mt-7 tracking-tight"
              >
                {t("hero.heading.line1")} <em className="not-italic text-[#D4AF37] font-display">{t("hero.heading.wealth")}</em>
                <br />
                {t("hero.heading.line2")} <span className="font-script italic text-[#F0D788]">{t("hero.heading.value")}</span>.
                <br />
                {t("hero.heading.line3")}
              </h1>
            </Reveal>

            <Reveal delay={220}>
              <p className="mt-5 md:mt-7 text-white/75 fluid-lead max-w-2xl">
                {t("hero.sub")}
              </p>
            </Reveal>

            <Reveal delay={320}>
              <div className="mt-7 md:mt-9 flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 md:gap-4">
                <a
                  data-testid="hero-cta-apply"
                  href="/apply/loan"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-7 py-3.5 rounded-full btn-gold text-sm font-semibold min-h-[48px]"
                >
                  {t("hero.cta.apply")}
                  <ArrowRight size={16} aria-hidden="true" />
                </a>
                <a
                  data-testid="hero-cta-associate"
                  href="/apply/dsa"
                  className="inline-flex items-center justify-center gap-2 px-6 md:px-7 py-3.5 rounded-full btn-outline-gold text-sm font-semibold min-h-[48px]"
                >
                  {t("hero.cta.associate")}
                </a>
              </div>
            </Reveal>

            <Reveal delay={420}>
              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-x-5 gap-y-3 text-white/65 text-[11px] sm:text-xs uppercase tracking-[0.14em] sm:tracking-[0.18em]">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck size={14} className="text-[#D4AF37]" /> {t("usp.noCibil")}
                </span>
                {/* LR-005: Acuité rating chip hidden pending current rating letter */}
                <span className="inline-flex items-center gap-2">
                  <TrendingUp size={14} className="text-[#D4AF37]" /> {t("usp.rates")}
                </span>
              </div>
            </Reveal>

            {/* Signature taglines — moved down-left into the left column */}
            <Reveal delay={520}>
              <div
                data-testid="hero-taglines"
                className="mt-12 md:mt-14 border-l-2 border-[#D4AF37]/50 pl-5"
              >
                <p className="font-script italic text-[#F0D788] text-xl sm:text-2xl md:text-3xl leading-snug">
                  તમારા વિશ્વાસની અમારી સાથે સહયાત્રા
                </p>
                <p className="mt-1 text-white/65 text-sm tracking-wide">
                  Your trust · Our service · Your success
                </p>
              </div>
            </Reveal>
          </div>

          {/* Right visual: stacked stat cards */}
          <div className="lg:col-span-5 min-w-0">
            <Reveal delay={150}>
              <div className="relative">
                {/* Main glass card */}
                <div className="relative rounded-3xl p-5 sm:p-7 lg:p-8 md:pb-14 lg:pb-16 gold-ring bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-md">
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="text-[11px] tracking-[0.25em] uppercase text-[#D4AF37]">
                        Our Impact
                      </div>
                      <div className="font-display text-xl sm:text-2xl mt-2">{t("hero.legacy")}</div>
                    </div>
                    <Logo size={104} className="sm:!w-[140px] sm:!h-[140px] lg:!w-[168px] lg:!h-[168px]" testId="hero-emblem" />
                  </div>

                  <div className="mt-6 sm:mt-7 grid grid-cols-2 gap-3 sm:gap-5">
                    {stats.map((s) => (
                      <div
                        key={s.v}
                        data-testid={`hero-stat-${s.v.toLowerCase().replace(/\s+/g, "-")}`}
                        className="rounded-2xl p-4 sm:p-5 border border-[#D4AF37]/20 bg-white/[0.03] min-w-0"
                      >
                        <div className="font-display text-xl sm:text-2xl text-[#F0D788]">{s.k}</div>
                        <div className="text-[11px] sm:text-[12px] text-white/60 mt-1 uppercase tracking-wider">
                          {s.v}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-7 h-px shimmer rounded-full" />

                  {/* Eligibility CTA — headline hero-adjacent placement (Feb 2026 UX pass).
                      Made deliberately prominent per user request: gradient card, pulsing
                      accent dot, larger padding, live badge. */}
                  <a
                    href="/eligibility"
                    data-testid="hero-eligibility-cta"
                    aria-label="Check my loan eligibility for ₹1,500"
                    className="mt-6 group relative flex items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-[#D4AF37] via-[#F0D788] to-[#D4AF37] text-[#0A1F44] px-4 sm:px-6 py-5 shadow-[0_18px_38px_-14px_rgba(212,175,55,0.55)] hover:brightness-105 transition min-h-[64px] ring-1 ring-[#F0D788]/60 focus-visible:ring-4 focus-visible:ring-[#F0D788]"
                  >
                    <span className="absolute -top-2 left-4 sm:left-6 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-[#0A1F44] text-[10px] uppercase tracking-[0.18em] text-[#F0D788] font-bold shadow keep-nowrap whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" aria-hidden="true" />
                      New · 5-6 working days
                    </span>
                    <div className="text-left min-w-0">
                      <div className="font-display text-lg sm:text-xl md:text-2xl leading-tight font-semibold">
                        Check My Loan Eligibility
                      </div>
                      <div className="text-[13px] mt-1 opacity-85">
                        One-time <strong>₹1,500</strong> · Emailed verdict · Non-refundable
                      </div>
                    </div>
                    <ArrowRight size={22} className="shrink-0 transition-transform group-hover:translate-x-1" aria-hidden="true" />
                  </a>

                  <p className="mt-5 text-white/55 text-xs">
                    Trusted lending across Gujarat since 2021.
                  </p>
                </div>

                {/* Floating accent card */}
                <div className="absolute -bottom-8 -left-6 hidden md:flex items-center gap-3 px-5 py-3.5 rounded-2xl bg-[#0A1F44] border border-[#D4AF37]/50 shadow-xl">
                  <div className="w-9 h-9 rounded-full grid place-items-center bg-[#D4AF37]/15 text-[#D4AF37]">
                    <BadgeCheck size={18} />
                  </div>
                  <div>
                    <div className="text-[11px] tracking-wider uppercase text-white/60">
                      Approval
                    </div>
                    <div className="font-display text-base text-[#F0D788]">Quick &amp; Fair*</div>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Marquee strip */}
      <div className="relative border-y border-[#D4AF37]/20 bg-[#04102B]/70 py-4">
        <div className="marquee text-[#D4AF37]/80 font-display text-sm tracking-[0.25em] uppercase">
          {[...Array(2)].map((_, i) => (
            <div className="marquee-track" key={`track-${i === 0 ? "A" : "B"}`}>
              <span>No CIBIL Required</span><span>·</span>
              <span>ROI 6.5% – 15%</span><span>·</span>
              <span>Building Wealth</span><span>·</span>
              <span>Securing Futures</span><span>·</span>
              <span>Creating Value</span><span>·</span>
              <span>Sahyog Finance &amp; Noble Infinity</span><span>·</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
